import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ActiveCell, RowMeta, TableId } from '../types';

const BUFFER = 16;
const PADDING = 16;
/** Clamp value to [min, max]; used so arrow keys don't move focus outside table bounds. */
function clamp(x: number, min: number, max: number): number {
  return Math.min(Math.max(x, min), max);
}

/** Key for cellRefMap: uniquely identifies a cell by row and column for keyboard nav focus. */
function cellKey(rowId: TableId, colId: TableId): string {
  return `${rowId}::${colId}`;
}

/** Shared keyboard handler for roving tabindex: arrows + Home/End. */
function createTableKeyDownHandler(
  move: (dRow: number, dCol: number) => void,
  setActive: React.Dispatch<React.SetStateAction<ActiveCell | null>>,
  visibleRows: RowMeta[],
  colIds: TableId[]
): (e: React.KeyboardEvent) => void {
  return (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName !== 'TH' && target.tagName !== 'TD') return;
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        move(-1, 0);
        break;
      case 'ArrowDown':
        e.preventDefault();
        move(1, 0);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        move(0, -1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        move(0, 1);
        break;
      case 'Home':
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
          setActive((a: ActiveCell | null) => (a ? { rowId: visibleRows[0].rowId, colId: a.colId } : a));
        } else {
          setActive((a: ActiveCell | null) => (a ? { rowId: a.rowId, colId: colIds[0] } : a));
        }
        break;
      case 'End':
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
          setActive((a) => (a ? { rowId: visibleRows[visibleRows.length - 1].rowId, colId: a.colId } : a));
        } else {
          setActive((a) => (a ? { rowId: a.rowId, colId: colIds[colIds.length - 1] } : a));
        }
        break;
      default:
        break;
    }
  };
}

/**
 * Scroll the row containing the active cell into view inside the table scroll container.
 * Uses container.scrollTop so sticky header/first column don't block scrolling (unlike scrollIntoView on the cell).
 * Container is the wrapper with data-table-scroll-container or the table's parent (.tableContainer).
 */
export function scrollRowIntoContainerView(cellEl: HTMLElement): void {
  const container =
    cellEl.closest('[data-table-scroll-container]') ?? cellEl.closest('table')?.parentElement;
  if (!container) return;

  const row = cellEl.closest('tr') as HTMLElement | null;
  if (!row) return;

  const rowRect = row.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  const topOverflow = rowRect.top < containerRect.top + BUFFER;
  const bottomOverflow = rowRect.bottom > containerRect.bottom - BUFFER;


  if (topOverflow) {
    container.scrollTop -= containerRect.top - rowRect.top + PADDING + BUFFER;
  } else if (bottomOverflow) {
    container.scrollTop += rowRect.bottom - containerRect.bottom + PADDING + BUFFER;
  }
}

/** Shared keyboard nav state and handlers for roving tabindex. */
export function useTableKeyboardNav(visibleRows: RowMeta[], colIds: TableId[]) {
  const cellRefMap = useRef(new Map<string, HTMLTableCellElement>());
  const hasMountedRef = useRef(false);
  const [active, setActive] = useState<ActiveCell | null>(() =>
    visibleRows.length > 0 && colIds.length > 0
      ? { rowId: visibleRows[0].rowId, colId: colIds[0] }
      : null
  );

  const registerCellRef = useCallback((rowId: TableId, colId: TableId, el: HTMLTableCellElement | null) => {
    const key = cellKey(rowId, colId);
    if (el) cellRefMap.current.set(key, el);
    else cellRefMap.current.delete(key);
  }, []);

  const onCellFocus = useCallback((rowId: TableId, colId: TableId) => {
    setActive((prev) => (prev?.rowId === rowId && prev?.colId === colId ? prev : { rowId, colId }));
  }, []);

  const move = useCallback(
    (dRow: number, dCol: number) => {
      if (!active || visibleRows.length === 0 || colIds.length === 0) return;
      const rowIndex = visibleRows.findIndex((r) => r.rowId === active.rowId);
      const colIndex = colIds.indexOf(active.colId);
      if (rowIndex < 0 || colIndex < 0) return;
      const newRowIndex = clamp(rowIndex + dRow, 0, visibleRows.length - 1);
      const newColIndex = clamp(colIndex + dCol, 0, colIds.length - 1);
      setActive({
        rowId: visibleRows[newRowIndex].rowId,
        colId: colIds[newColIndex]
      });
    },
    [active, visibleRows, colIds]
  );

  useEffect(() => {
    if (!active) return;
    if (!hasMountedRef.current) {
      // Keep initial roving tabindex state, but do not auto-focus on first load.
      hasMountedRef.current = true;
      return;
    }
    const el = cellRefMap.current.get(cellKey(active.rowId, active.colId));
    if (el) {
      el.focus();
      scrollRowIntoContainerView(el);
    }
  }, [active]);

  const handleKeyDown = useCallback(
    createTableKeyDownHandler(move, setActive, visibleRows, colIds),
    [move, visibleRows, colIds]
  );

  return { active, registerCellRef, onCellFocus, handleKeyDown };
}
